import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { customersAPI, tasksAPI, opportunitiesAPI, emailAPI, emailTemplatesAPI, gdprAPI, intelligenceAPI } from '../../services/api';
import AdminSidebar from '../../components/AdminSidebar';
import { useToast } from '../../contexts/ToastContext';
import {
  ArrowLeft, User, Mail, Phone, Building2, Globe,
  Clock, Tag, Edit2, Save, X, Plus, CheckSquare,
  Activity as ActivityIcon, FileText, Package, Receipt,
  Target, MessageSquare, RefreshCw, Trash2, Check,
  Send, Download, Sparkles, AlertTriangle
} from 'lucide-react';
import '../Admin/AdminDashboard.css';

const TABS = ['Activity', 'Tasks', 'Opportunities', 'Quotes', 'Orders', 'Notes'];

const STAGE_META = {
  new:          { label: 'New',          color: '#3b82f6' },
  qualified:    { label: 'Qualified',    color: '#8b5cf6' },
  quoted:       { label: 'Quoted',       color: '#f59e0b' },
  negotiating:  { label: 'Negotiating',  color: '#f97316' },
  won:          { label: 'Won',          color: '#10b981' },
  lost:         { label: 'Lost',         color: '#ef4444' },
};

const ACTIVITY_ICON = {
  email_sent: '📤', email_received: '📥', note: '📝',
  status_change: '🔄', quote_sent: '📋', order_created: '📦',
  payment_received: '💶', ai_action: '🤖', task_created: '✅',
  opportunity_won: '🏆', opportunity_lost: '❌', call_logged: '📞',
  whatsapp_sent: '💬', whatsapp_received: '💬',
};

const AdminCustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Activity');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Task creation state
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: '', type: 'follow_up', dueAt: '', priority: 'medium', notes: '' });

  // Opportunity creation state
  const [showOppForm, setShowOppForm] = useState(false);
  const [oppForm, setOppForm] = useState({ title: '', stage: 'new', value: '', probability: 10, expectedCloseAt: '' });

  // Note composition
  const [noteText, setNoteText] = useState('');

  // Send email modal (Phase 5)
  const [showEmail, setShowEmail] = useState(false);

  // Score recompute action state (Phase 8)
  const [scoringNow, setScoringNow] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await customersAPI.get360(id);
      setData(res.data);
      setEditForm({
        name: res.data.customer?.name || '',
        phone: res.data.customer?.phone || '',
        company: res.data.customer?.company || '',
        language: res.data.customer?.language || 'it',
        tags: (res.data.customer?.tags || []).join(', '),
      });
    } catch {
      toast.error('Failed to load customer profile');
    } finally {
      setLoading(false);
    }
  }, [id]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const saveEdit = async () => {
    try {
      const payload = {
        ...editForm,
        tags: editForm.tags.split(',').map(t => t.trim()).filter(Boolean),
      };
      await customersAPI.update(id, payload);
      toast.success('Customer updated');
      setEditing(false);
      load();
    } catch {
      toast.error('Update failed');
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await customersAPI.addNote(id, noteText.trim());
      toast.success('Note added');
      setNoteText('');
      load();
    } catch {
      toast.error('Failed to add note');
    }
  };

  const createTask = async () => {
    if (!taskForm.title) return;
    try {
      await tasksAPI.create({ ...taskForm, customerId: id });
      toast.success('Task created');
      setTaskForm({ title: '', type: 'follow_up', dueAt: '', priority: 'medium', notes: '' });
      setShowTaskForm(false);
      load();
    } catch {
      toast.error('Failed to create task');
    }
  };

  const completeTask = async (taskId) => {
    try {
      await tasksAPI.update(taskId, { isCompleted: true });
      toast.success('Task completed');
      load();
    } catch {
      toast.error('Failed to update task');
    }
  };

  const createOpportunity = async () => {
    if (!oppForm.title) return;
    try {
      await opportunitiesAPI.create({ ...oppForm, customerId: id, value: Number(oppForm.value) || 0 });
      toast.success('Opportunity created');
      setOppForm({ title: '', stage: 'new', value: '', probability: 10, expectedCloseAt: '' });
      setShowOppForm(false);
      load();
    } catch {
      toast.error('Failed to create opportunity');
    }
  };

  const updateOppStage = async (oppId, stage) => {
    try {
      await opportunitiesAPI.update(oppId, { stage });
      toast.success('Stage updated');
      load();
    } catch {
      toast.error('Failed to update stage');
    }
  };

  if (loading) return (
    <div className="admin-layout">
      <AdminSidebar />
      <main className="admin-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <RefreshCw size={28} color="#8b5cf6" style={{ animation: 'spin 1s linear infinite' }} />
      </main>
    </div>
  );

  const { customer, leads = [], quotes = [], orders = [], activities = [], tasks = [], opportunities = [] } = data || {};

  const pendingTasks = tasks.filter(t => !t.isCompleted);
  const completedTasks = tasks.filter(t => t.isCompleted);

  const segColor = {
    new: '#3b82f6', occasional: '#f59e0b', repeat: '#8b5cf6', vip: '#f97316', 'at-risk': '#ef4444'
  }[customer?.segment] || '#6b7280';

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <main className="admin-content">
        {/* Back button */}
        <button
          onClick={() => navigate('/admin/customers')}
          style={{ background: 'none', border: 'none', color: '#a1a1aa', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20, fontSize: 14 }}
        >
          <ArrowLeft size={16} /> Back to Customers
        </button>

        {/* Header card */}
        <div style={{ background: '#111', borderRadius: 16, border: '1px solid #27272a', padding: 28, marginBottom: 24, display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Avatar */}
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: `${segColor}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <User size={28} color={segColor} />
          </div>

          {/* Info */}
          <div style={{ flex: 1, minWidth: 200 }}>
            {editing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { key: 'name', label: 'Name', icon: <User size={14}/> },
                  { key: 'phone', label: 'Phone', icon: <Phone size={14}/> },
                  { key: 'company', label: 'Company', icon: <Building2 size={14}/> },
                  { key: 'tags', label: 'Tags (comma-separated)', icon: <Tag size={14}/> },
                ].map(f => (
                  <label key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                    <span style={{ color: '#71717a', display: 'flex', alignItems: 'center', gap: 4 }}>{f.icon} {f.label}</span>
                    <input
                      value={editForm[f.key] || ''}
                      onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                      style={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 6, padding: '8px 12px', color: '#fff', fontSize: 14 }}
                    />
                  </label>
                ))}
                <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                  <button onClick={saveEdit} style={{ background: '#10b981', border: 'none', borderRadius: 6, padding: '8px 16px', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}><Save size={14}/> Save</button>
                  <button onClick={() => setEditing(false)} style={{ background: '#27272a', border: 'none', borderRadius: 6, padding: '8px 16px', color: '#a1a1aa', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}><X size={14}/> Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                  <h2 style={{ margin: 0, color: '#fff', fontSize: 24 }}>{customer?.name || customer?.email}</h2>
                  <span style={{ padding: '3px 12px', borderRadius: 12, background: `${segColor}20`, color: segColor, fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>
                    {customer?.segment}
                  </span>
                  <button onClick={() => setEditing(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#71717a' }}>
                    <Edit2 size={14} />
                  </button>
                  {typeof customer?.score === 'number' && (
                    <span title="AI customer score (RFM)" style={{ marginLeft: 6, padding: '3px 10px', borderRadius: 12, background: customer.score >= 70 ? '#10b98122' : customer.score >= 40 ? '#f59e0b22' : '#27272a', color: customer.score >= 70 ? '#10b981' : customer.score >= 40 ? '#f59e0b' : '#a1a1aa', fontSize: 11, fontWeight: 700 }}>
                      <Sparkles size={10} style={{ marginRight: 4 }}/>{customer.score}
                    </span>
                  )}
                  {customer?.churnRisk && (
                    <span title="Churn risk" style={{ padding: '3px 10px', borderRadius: 12, background: '#ef444422', color: '#ef4444', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <AlertTriangle size={10}/> AT RISK
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <button onClick={() => setShowEmail(true)} style={actionBtn('#f97316')}>
                    <Send size={12}/> Send Email
                  </button>
                  <button
                    onClick={async () => { setScoringNow(true); try { await intelligenceAPI.scoreNow(id); toast.success('Score recomputed'); load(); } catch { toast.error('Failed'); } finally { setScoringNow(false); } }}
                    disabled={scoringNow}
                    style={actionBtn('#3b82f6')}>
                    <Sparkles size={12}/> {scoringNow ? 'Scoring…' : 'Recompute score'}
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const res = await gdprAPI.exportCustomer(id);
                        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
                        const a = document.createElement('a'); a.href = url; a.download = `customer-${id}-export.json`; a.click(); window.URL.revokeObjectURL(url);
                        toast.success('Export downloaded');
                      } catch { toast.error('Export failed'); }
                    }}
                    style={actionBtn('#71717a')}>
                    <Download size={12}/> GDPR Export
                  </button>
                  <button
                    onClick={async () => {
                      const reason = window.prompt('Reason for GDPR deletion?'); if (!reason) return;
                      try { await gdprAPI.deleteCustomer(id, reason); toast.success('Customer redacted'); navigate('/admin/customers'); } catch { toast.error('Delete failed'); }
                    }}
                    style={actionBtn('#ef4444')}>
                    <Trash2 size={12}/> GDPR Delete
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', color: '#a1a1aa', fontSize: 13 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Mail size={13}/>{customer?.email}</span>
                  {customer?.phone && <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Phone size={13}/>{customer.phone}</span>}
                  {customer?.company && <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Building2 size={13}/>{customer.company}</span>}
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Globe size={13}/>{customer?.language?.toUpperCase()}</span>
                </div>
                {(customer?.tags || []).length > 0 && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                    {customer.tags.map(tag => (
                      <span key={tag} style={{ padding: '2px 10px', borderRadius: 10, background: '#27272a', color: '#a1a1aa', fontSize: 12 }}>
                        <Tag size={9} style={{ marginRight: 3 }}/>{tag}
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {[
              { label: 'Orders', value: customer?.lifetimeStats?.orderCount || 0, color: '#3b82f6' },
              { label: 'Revenue', value: `€${(customer?.lifetimeStats?.revenue || 0).toFixed(0)}`, color: '#10b981' },
              { label: 'Avg Order', value: `€${(customer?.lifetimeStats?.avgOrderValue || 0).toFixed(0)}`, color: '#f59e0b' },
              { label: 'Opportunities', value: opportunities.length, color: '#8b5cf6' },
            ].map(s => (
              <div key={s.label} style={{ textAlign: 'center', padding: '12px 20px', background: '#0a0a0a', borderRadius: 10, border: '1px solid #27272a' }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 12, color: '#71717a', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #27272a', marginBottom: 24 }}>
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer',
                color: activeTab === tab ? '#a78bfa' : '#71717a',
                borderBottom: activeTab === tab ? '2px solid #8b5cf6' : '2px solid transparent',
                fontSize: 14, fontWeight: activeTab === tab ? 600 : 400,
                transition: 'all 0.15s'
              }}
            >
              {tab}
              {tab === 'Tasks' && pendingTasks.length > 0 && (
                <span style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 10, background: '#f59e0b20', color: '#f59e0b', fontSize: 11, fontWeight: 700 }}>
                  {pendingTasks.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'Activity' && (
          <div>
            {activities.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#52525b' }}>No activity recorded yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0, position: 'relative' }}>
                {activities.map((a, i) => (
                  <div key={a._id} style={{ display: 'flex', gap: 16, padding: '16px 0', borderBottom: '1px solid #1c1c1c' }}>
                    <div style={{ flexShrink: 0, width: 36, height: 36, borderRadius: '50%', background: '#27272a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
                      {ACTIVITY_ICON[a.type] || '📌'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <span style={{ fontWeight: 600, color: '#fff', fontSize: 14 }}>{a.subject || a.type?.replace(/_/g, ' ')}</span>
                        <span style={{ color: '#52525b', fontSize: 12 }}>
                          {new Date(a.occurredAt).toLocaleString('it-IT', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })}
                        </span>
                      </div>
                      {a.body && <div style={{ color: '#a1a1aa', fontSize: 13, marginTop: 2 }}>{a.body}</div>}
                      {a.actor && <div style={{ color: '#52525b', fontSize: 12, marginTop: 2 }}>by {a.actor}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'Tasks' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: '#fff' }}>Tasks ({pendingTasks.length} pending)</h3>
              <button
                onClick={() => setShowTaskForm(p => !p)}
                style={{ background: '#8b5cf6', border: 'none', borderRadius: 8, padding: '8px 16px', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
              >
                <Plus size={14}/> New Task
              </button>
            </div>

            {showTaskForm && (
              <div style={{ background: '#18181b', borderRadius: 10, border: '1px solid #27272a', padding: 20, marginBottom: 20 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {[
                    { key: 'title', label: 'Title', type: 'text', full: true },
                    { key: 'type', label: 'Type', type: 'select', options: ['follow_up','quote_follow_up','payment_chase','testimonial_request','call','email','meeting','other'] },
                    { key: 'dueAt', label: 'Due Date', type: 'datetime-local' },
                    { key: 'priority', label: 'Priority', type: 'select', options: ['low','medium','high'] },
                    { key: 'notes', label: 'Notes', type: 'text', full: true },
                  ].map(f => (
                    <label key={f.key} style={{ gridColumn: f.full ? '1 / -1' : undefined, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                      <span style={{ color: '#71717a' }}>{f.label}</span>
                      {f.type === 'select' ? (
                        <select
                          value={taskForm[f.key]}
                          onChange={e => setTaskForm(p => ({ ...p, [f.key]: e.target.value }))}
                          style={{ background: '#111', border: '1px solid #27272a', borderRadius: 6, padding: '8px 12px', color: '#fff', fontSize: 13 }}
                        >
                          {f.options.map(o => <option key={o} value={o}>{o.replace(/_/g,' ')}</option>)}
                        </select>
                      ) : (
                        <input
                          type={f.type}
                          value={taskForm[f.key]}
                          onChange={e => setTaskForm(p => ({ ...p, [f.key]: e.target.value }))}
                          style={{ background: '#111', border: '1px solid #27272a', borderRadius: 6, padding: '8px 12px', color: '#fff', fontSize: 13 }}
                        />
                      )}
                    </label>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button onClick={createTask} style={{ background: '#8b5cf6', border: 'none', borderRadius: 6, padding: '8px 16px', color: '#fff', cursor: 'pointer' }}>Create</button>
                  <button onClick={() => setShowTaskForm(false)} style={{ background: '#27272a', border: 'none', borderRadius: 6, padding: '8px 16px', color: '#a1a1aa', cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            )}

            {pendingTasks.length === 0 && !showTaskForm && (
              <div style={{ textAlign: 'center', padding: 32, color: '#52525b' }}>No pending tasks</div>
            )}
            {pendingTasks.map(task => (
              <div key={task._id} style={{ background: '#18181b', borderRadius: 10, border: '1px solid #27272a', padding: 16, marginBottom: 10, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <button
                  onClick={() => completeTask(task._id)}
                  style={{ flexShrink: 0, width: 24, height: 24, borderRadius: '50%', border: '2px solid #27272a', background: 'none', cursor: 'pointer', color: '#27272a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  title="Mark complete"
                >
                  <Check size={12} />
                </button>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#fff', fontSize: 14 }}>{task.title}</div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
                    <span style={{ color: '#71717a', fontSize: 12 }}>{task.type?.replace(/_/g,' ')}</span>
                    {task.dueAt && (
                      <span style={{ color: new Date(task.dueAt) < new Date() ? '#ef4444' : '#f59e0b', fontSize: 12 }}>
                        <Clock size={11} style={{ marginRight: 3 }}/>
                        {new Date(task.dueAt).toLocaleDateString('it-IT')}
                      </span>
                    )}
                    <span style={{
                      padding: '1px 8px', borderRadius: 8,
                      background: task.priority === 'high' ? '#ef444420' : task.priority === 'medium' ? '#f59e0b20' : '#27272a',
                      color: task.priority === 'high' ? '#ef4444' : task.priority === 'medium' ? '#f59e0b' : '#71717a',
                      fontSize: 11, fontWeight: 600
                    }}>{task.priority}</span>
                  </div>
                  {task.notes && <div style={{ color: '#71717a', fontSize: 12, marginTop: 4 }}>{task.notes}</div>}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'Opportunities' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: '#fff' }}>Opportunities ({opportunities.length})</h3>
              <button
                onClick={() => setShowOppForm(p => !p)}
                style={{ background: '#8b5cf6', border: 'none', borderRadius: 8, padding: '8px 16px', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
              >
                <Plus size={14}/> New Opportunity
              </button>
            </div>
            {showOppForm && (
              <div style={{ background: '#18181b', borderRadius: 10, border: '1px solid #27272a', padding: 20, marginBottom: 20 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {[
                    { key: 'title', label: 'Title', type: 'text', full: true },
                    { key: 'stage', label: 'Stage', type: 'select', options: Object.keys(STAGE_META) },
                    { key: 'value', label: 'Value (€)', type: 'number' },
                    { key: 'probability', label: 'Probability (%)', type: 'number' },
                    { key: 'expectedCloseAt', label: 'Expected Close', type: 'date' },
                  ].map(f => (
                    <label key={f.key} style={{ gridColumn: f.full ? '1 / -1' : undefined, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                      <span style={{ color: '#71717a' }}>{f.label}</span>
                      {f.type === 'select' ? (
                        <select
                          value={oppForm[f.key]}
                          onChange={e => setOppForm(p => ({ ...p, [f.key]: e.target.value }))}
                          style={{ background: '#111', border: '1px solid #27272a', borderRadius: 6, padding: '8px 12px', color: '#fff', fontSize: 13 }}
                        >
                          {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <input
                          type={f.type}
                          value={oppForm[f.key]}
                          onChange={e => setOppForm(p => ({ ...p, [f.key]: e.target.value }))}
                          style={{ background: '#111', border: '1px solid #27272a', borderRadius: 6, padding: '8px 12px', color: '#fff', fontSize: 13 }}
                        />
                      )}
                    </label>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button onClick={createOpportunity} style={{ background: '#8b5cf6', border: 'none', borderRadius: 6, padding: '8px 16px', color: '#fff', cursor: 'pointer' }}>Create</button>
                  <button onClick={() => setShowOppForm(false)} style={{ background: '#27272a', border: 'none', borderRadius: 6, padding: '8px 16px', color: '#a1a1aa', cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            )}
            {opportunities.length === 0 && !showOppForm && (
              <div style={{ textAlign: 'center', padding: 32, color: '#52525b' }}>No opportunities yet</div>
            )}
            {opportunities.map(opp => {
              const sm = STAGE_META[opp.stage] || STAGE_META.new;
              return (
                <div key={opp._id} style={{ background: '#18181b', borderRadius: 10, border: `1px solid ${sm.color}40`, padding: 16, marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 600, color: '#fff', fontSize: 15, marginBottom: 6 }}>{opp.title}</div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ padding: '2px 10px', borderRadius: 10, background: `${sm.color}20`, color: sm.color, fontSize: 12, fontWeight: 600 }}>{sm.label}</span>
                        <span style={{ color: '#10b981', fontSize: 13, fontWeight: 600 }}>€{(opp.value || 0).toFixed(0)}</span>
                        <span style={{ color: '#f59e0b', fontSize: 13 }}>{opp.probability}% probability</span>
                        {opp.expectedCloseAt && (
                          <span style={{ color: '#71717a', fontSize: 12 }}>Close: {new Date(opp.expectedCloseAt).toLocaleDateString('it-IT')}</span>
                        )}
                      </div>
                    </div>
                    <select
                      value={opp.stage}
                      onChange={e => updateOppStage(opp._id, e.target.value)}
                      style={{ background: '#111', border: `1px solid ${sm.color}`, borderRadius: 6, padding: '6px 10px', color: sm.color, fontSize: 12, cursor: 'pointer' }}
                      onClick={e => e.stopPropagation()}
                    >
                      {Object.entries(STAGE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'Quotes' && (
          <div>
            {quotes.length === 0
              ? <div style={{ textAlign: 'center', padding: 32, color: '#52525b' }}>No quotes found</div>
              : quotes.map(q => (
                <div key={q._id} style={{ background: '#18181b', borderRadius: 10, border: '1px solid #27272a', padding: 16, marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#fff' }}>Quote #{q.quoteNumber || q._id.slice(-6)}</div>
                    <div style={{ color: '#71717a', fontSize: 13, marginTop: 4 }}>{new Date(q.createdAt).toLocaleDateString('it-IT')}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#10b981', fontWeight: 700 }}>€{(q.totalAmount || q.total || 0).toFixed(2)}</div>
                    <span style={{ padding: '2px 8px', borderRadius: 8, background: '#27272a', color: '#a1a1aa', fontSize: 11 }}>{q.status}</span>
                  </div>
                </div>
              ))
            }
          </div>
        )}

        {activeTab === 'Orders' && (
          <div>
            {orders.length === 0
              ? <div style={{ textAlign: 'center', padding: 32, color: '#52525b' }}>No orders found</div>
              : orders.map(o => (
                <div key={o._id} style={{ background: '#18181b', borderRadius: 10, border: '1px solid #27272a', padding: 16, marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#fff' }}>Order #{o.orderNumber || o._id.slice(-6)}</div>
                    <div style={{ color: '#71717a', fontSize: 13, marginTop: 4 }}>{o.description || o.material}</div>
                    <div style={{ color: '#52525b', fontSize: 12 }}>{new Date(o.createdAt).toLocaleDateString('it-IT')}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#10b981', fontWeight: 700 }}>€{(o.total || 0).toFixed(2)}</div>
                    <span style={{ padding: '2px 8px', borderRadius: 8, background: '#27272a', color: '#a1a1aa', fontSize: 11 }}>{o.status}</span>
                  </div>
                </div>
              ))
            }
          </div>
        )}

        {activeTab === 'Notes' && (
          <div>
            <div style={{ background: '#18181b', borderRadius: 10, border: '1px solid #27272a', padding: 16, marginBottom: 20 }}>
              <textarea
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                placeholder="Add a note about this customer…"
                rows={3}
                style={{ width: '100%', background: '#111', border: '1px solid #27272a', borderRadius: 6, padding: 12, color: '#fff', fontSize: 13, resize: 'vertical', boxSizing: 'border-box', marginBottom: 10 }}
              />
              <button
                onClick={addNote}
                disabled={!noteText.trim()}
                style={{ background: noteText.trim() ? '#8b5cf6' : '#27272a', border: 'none', borderRadius: 6, padding: '8px 16px', color: noteText.trim() ? '#fff' : '#52525b', cursor: noteText.trim() ? 'pointer' : 'default' }}
              >
                <MessageSquare size={13} style={{ marginRight: 6 }}/> Add Note
              </button>
            </div>
            {(customer?.notes || []).length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: '#52525b' }}>No notes yet</div>
            ) : (
              [...(customer?.notes || [])].reverse().map((n, i) => (
                <div key={i} style={{ background: '#18181b', borderRadius: 10, border: '1px solid #27272a', padding: 16, marginBottom: 10 }}>
                  <div style={{ color: '#fff', fontSize: 14 }}>{n.content}</div>
                  <div style={{ color: '#52525b', fontSize: 12, marginTop: 6 }}>
                    {new Date(n.createdAt).toLocaleString('it-IT', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
      {showEmail && (
        <SendEmailModal
          customer={customer}
          onClose={() => setShowEmail(false)}
          onSent={() => { setShowEmail(false); load(); toast.success('Email sent ✓'); }}
        />
      )}
    </div>
  );
};

const actionBtn = (color) => ({
  background: `${color}20`, color, border: `1px solid ${color}40`, borderRadius: 6,
  padding: '5px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 5
});

// ─── Send Email Modal (Phase 5) ────────────────────────────────────────────
const SendEmailModal = ({ customer, onClose, onSent }) => {
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [templateKey, setTemplateKey] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    emailTemplatesAPI.getAll()
      .then(res => setTemplates((res.data.templates || res.data || []).filter(t => !customer?.language || t.language === customer.language)))
      .catch(() => {});
  }, [customer]);

  const interpolate = (str = '') => str
    .replace(/\{\{customer\.firstName\}\}/g, (customer?.name || '').split(' ')[0] || 'Cliente')
    .replace(/\{\{customer\.name\}\}/g, customer?.name || '')
    .replace(/\{\{customer\.company\}\}/g, customer?.company || '')
    .replace(/\{\{customer\.email\}\}/g, customer?.email || '');

  const pickTemplate = (key) => {
    setTemplateKey(key);
    const tpl = templates.find(t => t.key === key);
    if (tpl) { setSubject(interpolate(tpl.subject)); setBody(interpolate(tpl.body)); }
  };

  const send = async () => {
    if (!subject || !body) { toast.error('Subject and body required'); return; }
    setSending(true);
    try {
      await emailAPI.send({ customerId: customer._id, templateKey, subject, html: body });
      onSent();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background:'#0a0a0a', border:'1px solid #27272a', borderRadius:10, padding:24, width:'100%', maxWidth:760, maxHeight:'90vh', overflow:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h2 style={{ margin:0, fontSize:20, color:'#fff' }}>Send Email · {customer?.name || customer?.email}</h2>
          <button onClick={onClose} style={{ background:'transparent', border:'1px solid #27272a', color:'#a1a1aa', borderRadius:4, padding:'4px 8px', cursor:'pointer' }}><X size={16}/></button>
        </div>

        <div style={{ display:'grid', gap:12 }}>
          <div>
            <label style={lblS}>Template ({customer?.language?.toUpperCase()})</label>
            <select value={templateKey} onChange={e => pickTemplate(e.target.value)} style={inputS}>
              <option value="">— Compose freely —</option>
              {templates.map(t => <option key={t._id} value={t.key}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label style={lblS}>To</label>
            <input value={customer?.email || ''} disabled style={{ ...inputS, opacity: .6 }} />
          </div>
          <div>
            <label style={lblS}>Subject</label>
            <input value={subject} onChange={e => setSubject(e.target.value)} style={inputS} />
          </div>
          <div>
            <label style={lblS}>Body (HTML)</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} style={{ ...inputS, minHeight:240, fontFamily:'monospace', fontSize:12 }} />
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <button onClick={onClose} style={{ background:'#18181b', color:'#a1a1aa', border:'1px solid #27272a', padding:'8px 14px', borderRadius:6, cursor:'pointer' }}>Cancel</button>
            <button onClick={send} disabled={sending} style={{ background:'#f97316', color:'#fff', border:'none', padding:'8px 16px', borderRadius:6, cursor:'pointer', fontWeight:600, display:'inline-flex', alignItems:'center', gap:6 }}>
              <Send size={14}/> {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const lblS = { display:'block', fontSize:11, color:'#a1a1aa', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:4 };
const inputS = { width:'100%', padding:'8px 10px', background:'#18181b', border:'1px solid #27272a', borderRadius:6, color:'#fafafa', fontSize:13, boxSizing:'border-box' };

export default AdminCustomerDetail;
